/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { ValidatorFn } from '@/hooks/useForm'

import type { ShipmentFormValues } from './types'

const values = (formValues?: unknown) => (formValues ?? {}) as Partial<ShipmentFormValues>

const required =
  (message: string): ValidatorFn =>
  (value) =>
    value === null || value === undefined || (typeof value === 'string' && !value.trim()) ? message : undefined

/**
 * A location is only usable once a suggestion has been picked — free text on its
 * own gives us no coordinates, and `origin_location`/`destination_location` are
 * NOT NULL geography points.
 */
const locationPicked =
  (latField: keyof ShipmentFormValues, what: string): ValidatorFn =>
  (value, formValues) => {
    if (typeof value !== 'string' || !value.trim()) return `${what} location is required`
    return values(formValues)[latField] === null ? 'Pick an address from the list' : undefined
  }

const temperature =
  (message: string): ValidatorFn =>
  (value) => {
    if (typeof value !== 'string' || !value.trim()) return message
    return Number.isFinite(Number(value)) ? undefined : 'Must be a number'
  }

/**
 * Validators for the new-shipment form. Defined at module scope because
 * useForm treats the object as a dependency — rebuilding it per render would
 * make every callback it returns unstable.
 */
export const shipmentValidators: Record<string, ValidatorFn> = {
  shipmentName: required('Shipment ID is required'),
  customerId: required('Customer is required'),

  expectedStartAt: required('Expected start is required'),
  expectedEndAt: (value, formValues) => {
    if (!value) return 'Expected end is required'
    const start = values(formValues).expectedStartAt
    return start && (value as Date) <= start ? 'Must be after the expected start' : undefined
  },
  // A shipment may be created in any state so history can be backfilled, but the
  // actual times have to agree with the state — the server rejects the
  // combinations these guard against, and the map reads actualEndAt to decide
  // whether a shipment's readings are live or frozen.
  actualStartAt: (value, formValues) => {
    const { status, actualEndAt } = values(formValues)
    if (!value && (status === 'active' || status === 'completed')) {
      return `${status === 'active' ? 'An' : 'A'} ${status} shipment needs an actual start`
    }
    if (!value && actualEndAt) return 'Required when an actual end is set'
    if (value && status === 'planned') return 'Clear this, or set the status to Active'
    return undefined
  },
  actualEndAt: (value, formValues) => {
    const { status, actualStartAt } = values(formValues)
    if (!value) return status === 'completed' ? 'A completed shipment needs an actual end' : undefined
    if (status === 'active') return 'Clear this, or set the status to Completed'
    if (status === 'planned') return 'Clear this, or set the status to Completed'
    if (!actualStartAt) return 'Set the actual start first'
    return (value as Date) <= actualStartAt ? 'Must be after the actual start' : undefined
  },

  originName: required('Origin name is required'),
  originLocation: locationPicked('originLatitude', 'Origin'),
  destinationName: required('Destination name is required'),
  destinationLocation: locationPicked('destinationLatitude', 'Destination'),

  minTemperature: temperature('Min temperature is required'),
  maxTemperature: (value, formValues) => {
    const invalid = temperature('Max temperature is required')(value, formValues)
    if (invalid) return invalid
    const min = values(formValues).minTemperature
    if (!min?.trim() || !Number.isFinite(Number(min))) return undefined
    return Number(value) < Number(min) ? 'Must be at or above the min temperature' : undefined
  },
}
